import { useUserSession } from '@solinkcorp/authentication';
import { toastUnexpectedError } from '@solinkcorp/solink-components';
import PageLoadingSpinner from 'loadingIndicator/PageLoadingSpinner';
import React, { useContext, useEffect, useState } from 'react';
import { SolinkDexie } from './db';

export { SolinkDexie };

const DbContext = React.createContext<SolinkDexie | undefined>(undefined);

interface DbProviderProps {
  children: React.ReactNode;
}

/**
 * Render this in your tree to get access to the video-discovery IndexDB.
 */
export const DbProvider: React.FC<DbProviderProps> = ({ children }) => {
  const user = useUserSession();
  const [db, setDb] = useState<SolinkDexie | undefined>();

  useEffect(() => {
    loadDb(user.userId).then((db) => setDb(db), toastUnexpectedError);
  }, [user.userId]);

  // Don't render the app until we've loaded the database.
  if (!db) {
    return <PageLoadingSpinner />;
  }
  return <DbContext.Provider value={db}>{children}</DbContext.Provider>;
};

/**
 * Hook to access the video-discovery IndexDB.
 */
export function useDb(): SolinkDexie {
  const db = useContext(DbContext);
  if (!db) {
    throw new Error('Missing DB context');
  }
  return db;
}

async function loadDb(userId: string): Promise<SolinkDexie | undefined> {
  let hashedUserId;
  try {
    hashedUserId = await getHashedUserId(userId);
  } catch (err) {
    console.error('Failed to hash userId', err);
  }
  try {
    const db = new SolinkDexie(hashedUserId || userId);
    return db;
  } catch (err) {
    console.error('Failed to derive crypto key', err);
  }
}

// Hash the user ID so it's not directly exposed
async function getHashedUserId(userId: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(userId);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hash));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}
